import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useClickAway } from 'react-use';
import styled, { createGlobalStyle } from 'styled-components';
import { BOSS_ANNOUNCEMENT_ID } from 'ui/announcement/BossAnnouncement';
import { colors, extraLargeShadow } from 'ui/theme';
import { ModalContextProvider } from 'utils/contexts/modal';
import { pxToRem } from 'utils/convert';
import { useNavBarHeight } from 'utils/hooks/useNavBarHeight';
import { closeNav } from '../ducks/actions';
import { NAV_SLIDER_LEFT, NAV_SLIDER_MAP, NAV_SLIDER_RIGHT } from '../constants';
import { selectNavComponentKey } from '../ducks/selectors';
import { MENU_TRIGGER_TEXT } from './HamburgerMenuTrigger';
import { Modals } from './Modals';
import { NAVBAR_MENUS_TEXT } from './NavbarMenus';

const panelWidth = pxToRem(400);

enum SLIDER_POSITIONS {
  RIGHT = 'right',
  LEFT = 'left',
}

const StyledSliderContainer = styled.div<{ navBarHeight: number }>`
  position: fixed;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  width: ${panelWidth};
  max-width: 100%;
  overflow: hidden;
  padding-top: ${({ navBarHeight }) => pxToRem(navBarHeight)};
  background-color: ${colors.white};
  transition: transform 0.2s;
  will-change: transform;
  z-index: 999;

  &::-webkit-scrollbar {
    display: none;
  }

  &[data-position=${SLIDER_POSITIONS.RIGHT}] {
    right: -${panelWidth};

    &[aria-expanded='true'] {
      ${extraLargeShadow}
      transform: translateX(-${panelWidth});
    }
  }

  &[data-position=${SLIDER_POSITIONS.LEFT}] {
    left: -${panelWidth};

    &[aria-expanded='true'] {
      ${extraLargeShadow}
      transform: translateX(${panelWidth});
    }
  }
`;

const BodyStyles = createGlobalStyle<{ isMenuOpen?: boolean }>`
  body {
    overflow: ${(props) => (props.isMenuOpen ? 'hidden' : '')};
  }
`;

export const SLIDER_CONTAINER_TEXT = {
  leftId: 'navslider-container-left',
  rightId: 'navslider-container-right',
};

const SliderContainerBase = () => {
  const navBarHeight = useNavBarHeight();
  const dispatch = useDispatch();
  const componentKey = useSelector(selectNavComponentKey);
  // refs used to determine click outside range to close slider
  const containerRef = useRef(null);
  const navbarMenuRef = useRef(document.getElementById(NAVBAR_MENUS_TEXT.id)).current;
  const menuTriggerRef = useRef(document.getElementById(MENU_TRIGGER_TEXT.id)).current;
  const bossCalloutTriggerRef = useRef(document.getElementById(BOSS_ANNOUNCEMENT_ID)).current;

  const handleClickAway = (event: Event) => {
    const shouldHandleClick = [navbarMenuRef, menuTriggerRef, bossCalloutTriggerRef].some((el) =>
      el?.contains(event.target as HTMLElement),
    );

    if (!shouldHandleClick) {
      dispatch(closeNav());
    }
  };

  useClickAway(containerRef, handleClickAway, ['click']);

  const Component = NAV_SLIDER_MAP[componentKey as keyof typeof NAV_SLIDER_MAP];
  const shouldShowLeft = componentKey ? Object.keys(NAV_SLIDER_LEFT).includes(componentKey) : false;
  const shouldShowRight = componentKey
    ? Object.keys(NAV_SLIDER_RIGHT).includes(componentKey)
    : false;

  return (
    <>
      <div ref={containerRef}>
        <StyledSliderContainer
          data-position={SLIDER_POSITIONS.LEFT}
          aria-expanded={shouldShowLeft}
          data-test={SLIDER_CONTAINER_TEXT.leftId}
          navBarHeight={navBarHeight}
        >
          {Component && shouldShowLeft && <Component />}
        </StyledSliderContainer>
        <StyledSliderContainer
          data-position={SLIDER_POSITIONS.RIGHT}
          aria-expanded={shouldShowRight}
          data-test={SLIDER_CONTAINER_TEXT.rightId}
          navBarHeight={navBarHeight}
        >
          {Component && shouldShowRight && <Component />}
        </StyledSliderContainer>
      </div>
      <Modals />
      <BodyStyles isMenuOpen={shouldShowLeft || shouldShowRight} />
    </>
  );
};

export const SliderContainer = () => (
  <ModalContextProvider>
    <SliderContainerBase />
  </ModalContextProvider>
);
